import React from 'react';
import mixpanel from 'mixpanel-browser';
import { Timer, ArrowRightLeft, MapPin, Zap, AlertTriangle } from 'lucide-react';
import styled from "styled-components";
import { useNavigate } from 'react-router-dom';
import {
  serializeTransferStatsToSearchParams,
  transferStatsPayloadFromLegs,
} from '../../utils/transferStatsUrlParams';
import { useTranslation } from 'react-i18next';

import {
  Journey,
  TransferStat,
  PlatformTransfer,
  getTransferPlatformIndicator,
  JourneyWithHafas,
} from "../../utils/mapJourneysData";
import transferIcon from '../../icons/transfericon.svg'
import { colors } from "../../utils/colors";
import { getDurationInMinutes, formatDuration } from "../../utils/durationUtils";
import * as TypographyStyles from '../TypographyStyles';
import { getColor } from "../../utils/colorFormatter";
import { ReactSVG } from "react-svg";
import { StyledTooltip } from '../TooltipStyles';
import {
  StatContainer,
  StatsContainer,
  StatBox,
  StatTopSection,
  StatIconWrapper,
  StatTitle,
  StatValue,
  InsightsButton,
  formatPercentage,
  getColorVariants,
} from '../Elements/SharedStatComponents';


type TransferProps = {
  index: number;
  journey: JourneyWithHafas
  isLoading: boolean; // Accept the loading state
}

const TransferTimeContainer = styled('div')<{ color: string }>`
  color: ${props => props.color};
  display: flex;
  align-items: center;
  grid-column-gap: 8px;
  & svg {
    fill: ${colors.grey600};
    height: 14px; /* Set height to 14px */
    width: auto;  /* Maintain aspect ratio */
  }
`

const TransferBadge = styled(TypographyStyles.BodyMSemibold)`
  padding: 2px 12px;
  border-radius: 10px;
  font-weight: 600;
`;

const LikelyMissTransferBadge = styled(TransferBadge)`
  color: ${colors.black} !important;
  background-color: ${colors.yellow};
`;

const MissedTransferBadge = styled(TransferBadge)`
  color: ${colors.white} !important;
  background-color: ${colors.red};
`;

const InfeasibleTransferBadge = styled(TransferBadge)`
  color: ${colors.white} !important;
  background-color: ${colors.red};
`;

const TransferTimeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const TransferTimeArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlatformPositiveBadge = styled(TransferBadge)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${colors.white} !important;
  background-color: ${colors.green};
  flex-shrink: 0;

  & svg {
    flex-shrink: 0;
  }
`;

const DifferentStationPlatformBadge = styled(TransferBadge)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${colors.black} !important;
  background-color: ${colors.yellow};
  flex-shrink: 0;

  & svg {
    flex-shrink: 0;
  }
`;

type TransferTimeProps = { transferTime: number, type: string }

const TransferTime = ({ transferTime, type }: TransferTimeProps) => {
  const { t } = useTranslation();
  
  const color = () => {
    if (type === 'planned') return colors.black
    if (type === 'estimated' && transferTime < 0) return colors.red
    return getColor(transferTime, 'transferTimeInMinutes')
  }

  const transferTimeLabel = type === 'planned' 
    ? t('journeyDetails.transfer.plannedTransferTime')
    : t('journeyDetails.transfer.estimatedTransferTime');

  return (
    <TransferTimeContainer color={color()}>
      <ReactSVG src={transferIcon} />
      <TypographyStyles.BodyMSemibold>
        {formatDuration(transferTime)}
        <TypographyStyles.BodyMRegular 
          color={transferTime < 0 && type === 'estimated' ? colors.red : colors.black}> {transferTimeLabel}
        </TypographyStyles.BodyMRegular>
      </TypographyStyles.BodyMSemibold>
    </TransferTimeContainer>
  )
}

const Transfer = ({ index, journey, isLoading }: TransferProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Ensure trainLegs is correctly filtered
  const trainLegs = journey.legs.filter(leg => !leg.walking);

  // Guard access to avoid out-of-bounds errors
  if (index >= trainLegs.length - 1) {
    return null; // Return nothing if index is out of bounds
  }

  const currentLeg = trainLegs[index];
  const nextLeg = trainLegs[index + 1];
  
  // Check if stops are cancelled
  const isFirstLegDestinationCancelled = currentLeg?.cancelled === true || 
    (currentLeg?.plannedArrival && !currentLeg?.arrival && currentLeg?.arrivalDelay === null);
  const isSecondLegOriginCancelled = nextLeg?.cancelled === true || 
    (nextLeg?.plannedDeparture && !nextLeg?.departure && nextLeg?.departureDelay === null);
  const isInfeasible = isFirstLegDestinationCancelled || isSecondLegOriginCancelled;
  
  // Check if transfer is infeasible (arrival > departure)
  const actualTransferStart = currentLeg.arrival || currentLeg.plannedArrival;
  const actualTransferEnd = nextLeg.departure || nextLeg.plannedDeparture;
  let isTransferInfeasible = false;
  let isSecondLegDepartureInPast = false;
  
  if (actualTransferStart && actualTransferEnd) {
    const arrivalTime = new Date(actualTransferStart);
    const departureTime = new Date(actualTransferEnd);
    isTransferInfeasible = arrivalTime > departureTime;
    isSecondLegDepartureInPast = departureTime < new Date();
  }
  
  // Determine which badge to show (replacing only the transfer time text, keeping the icon)
  let transferTimeContent = null;
  const transferBadgeTooltipId = `transfer-badge-tooltip-${journey.id}-${index}`;
  
  if (isInfeasible) {
    transferTimeContent = (
      <>
        <InfeasibleTransferBadge data-tooltip-id={transferBadgeTooltipId}>
          {t('journeyDetails.transfer.infeasible')}
        </InfeasibleTransferBadge>
        <StyledTooltip id={transferBadgeTooltipId} place="top" className="tooltip-styled">
          {t('journeyDetails.transfer.infeasibleTooltip')}
        </StyledTooltip>
      </>
    );
  } else if (isTransferInfeasible) {
    if (isSecondLegDepartureInPast) {
      // Transfer missed
      transferTimeContent = (
        <>
          <MissedTransferBadge data-tooltip-id={transferBadgeTooltipId}>
            {t('journeyDetails.transfer.missed')}
          </MissedTransferBadge>
          <StyledTooltip id={transferBadgeTooltipId} place="top" className="tooltip-styled">
            {t('journeyDetails.transfer.missedTooltip')}
          </StyledTooltip>
        </>
      );
    } else {
      // Transfer likely to be missed
      transferTimeContent = (
        <>
          <LikelyMissTransferBadge data-tooltip-id={transferBadgeTooltipId}>
            {t('journeyDetails.transfer.likelyMiss')}
          </LikelyMissTransferBadge>
          <StyledTooltip id={transferBadgeTooltipId} place="top" className="tooltip-styled">
            {t('journeyDetails.transfer.likelyMissTooltip')}
          </StyledTooltip>
        </>
      );
    }
  } else {
    // Show normal transfer time
    const actualTransferTime = actualTransferStart && actualTransferEnd 
      ? getDurationInMinutes(actualTransferStart, actualTransferEnd) 
      : null;
    const hasDelay = currentLeg?.arrivalDelay != null && nextLeg?.departureDelay != null;
    
    if (actualTransferTime !== null) {
      transferTimeContent = <TransferTime transferTime={actualTransferTime} type={hasDelay ? 'estimated' : 'planned'} />;
    } else {
      const plannedTransferStart = currentLeg?.plannedArrival;
      const plannedTransferEnd = nextLeg?.plannedDeparture;
      const plannedTransferTime = plannedTransferStart && plannedTransferEnd ? 
        getDurationInMinutes(plannedTransferStart, plannedTransferEnd) : 0;
      transferTimeContent = <TransferTime transferTime={plannedTransferTime} type="planned" />;
    }
  }
  
  // Create the transfer time component - always show icon, replace text with badge when needed
  const TransferTimeComponent = () => {
    // If showing a badge, show icon + badge
    if (isInfeasible || isTransferInfeasible) {
      return (
        <TransferTimeContainer color={colors.black}>
          <ReactSVG src={transferIcon} />
          {transferTimeContent}
        </TransferTimeContainer>
      );
    }
    // Otherwise, use the normal TransferTime component which already includes the icon
    return transferTimeContent;
  };

  const transferStats = trainLegs[index].transferStats;
  const showTransferStats = !isLoading && transferStats != null;

  const handleSeeMoreClick = () => {
    // Send Mixpanel event
    mixpanel.track('Transfer Statistics Opened', {
      firstLegLineName: trainLegs[index].line.name,
      secondLegLineName: trainLegs[index + 1].line.name,
      firstLegTransferStation: trainLegs[index].destination.name,
      secondLegTransferStation: trainLegs[index + 1].origin.name
    });
    
    const first = trainLegs[index];
    const second = trainLegs[index + 1];
    const search = serializeTransferStatsToSearchParams(
      transferStatsPayloadFromLegs(first, second)
    ).toString();
    navigate(
      { pathname: '/transfer-statistics', search },
      { state: { from: window.location.pathname, firstLeg: first, secondLeg: second } }
    );
  };

  // Calculate stats for display
  const avgTransferTime = !isLoading && transferStats?.avgSecondsToChange != null 
    ? Math.round(transferStats.avgSecondsToChange / 60) 
    : null;
  const avgTransferTimeColor = avgTransferTime === null ? colors.grey600 : getColor(avgTransferTime, 'transferTimeInMinutes');
  const avgTransferTimeValueColor = getColorVariants(avgTransferTimeColor);
  
  const missedConnections = !isLoading && transferStats?.percentageMissedConnections != null 
    ? Math.round(transferStats.percentageMissedConnections) 
    : null;
  const missedConnectionsColor = missedConnections === null ? colors.grey600 : getColor(missedConnections, 'missedTransfersPercentage');
  const missedConnectionsValueColor = getColorVariants(missedConnectionsColor);

  const hasStats = avgTransferTime != null || missedConnections != null;

  const hidePlatformBadge = isInfeasible || isTransferInfeasible;
  const platformTransfer = currentLeg?.platformTransfer;
  const platformIndicator = getTransferPlatformIndicator(
    platformTransfer,
    hidePlatformBadge
  );
  const platformTooltipId = `transfer-platform-tooltip-${journey.id}-${index}`;

  const platformBadgeEl =
    platformIndicator === 'same-track' ? (
      <>
        <PlatformPositiveBadge data-tooltip-id={platformTooltipId}>
          <MapPin size={14} strokeWidth={2.5} aria-hidden />
          {t('journeyDetails.transfer.sameTrack')}
        </PlatformPositiveBadge>
        <StyledTooltip id={platformTooltipId} place="top" className="tooltip-styled">
          {t('journeyDetails.transfer.sameTrackTooltip')}
        </StyledTooltip>
      </>
    ) : platformIndicator === 'same-platform' ? (
      <>
        <PlatformPositiveBadge data-tooltip-id={platformTooltipId}>
          <Zap size={14} strokeWidth={2.5} aria-hidden fill="currentColor" />
          {t('journeyDetails.transfer.samePlatform')}
        </PlatformPositiveBadge>
        <StyledTooltip id={platformTooltipId} place="top" className="tooltip-styled">
          {t('journeyDetails.transfer.samePlatformTooltip')}
        </StyledTooltip>
      </>
    ) : platformIndicator === 'different-station' ? (
      <>
        <DifferentStationPlatformBadge data-tooltip-id={platformTooltipId}>
          <AlertTriangle size={14} strokeWidth={2.5} aria-hidden />
          {t('journeyDetails.transfer.differentStation')}
        </DifferentStationPlatformBadge>
        <StyledTooltip id={platformTooltipId} place="top" className="tooltip-styled">
          {t('journeyDetails.transfer.differentStationTooltip', {
            'station name': nextLeg?.origin?.name ?? '',
          })}
        </StyledTooltip>
      </>
    ) : null;

  return (
    <React.Fragment>
      <TransferTimeRow>
        <TransferTimeArea>
          <TransferTimeComponent />
        </TransferTimeArea>
        {platformBadgeEl}
      </TransferTimeRow>
      {showTransferStats && hasStats && (
        <StatContainer $clickable onClick={handleSeeMoreClick}>
          <StatsContainer>
            {avgTransferTime != null && (
              <StatBox>
                <StatTopSection>
                  <StatIconWrapper $iconColor={avgTransferTimeColor}>
                    <Timer size={16} />
                  </StatIconWrapper>
                  <StatValue $valueColor={avgTransferTimeValueColor}>
                    {formatDuration(avgTransferTime)}
                  </StatValue>
                </StatTopSection>
                <StatTitle>{t('journeyDetails.transfer.avgTime')}</StatTitle>
              </StatBox>
            )}
            
            {missedConnections != null && (
              <StatBox>
                <StatTopSection>
                  <StatIconWrapper $iconColor={missedConnectionsColor}>
                    <ArrowRightLeft size={16} />
                  </StatIconWrapper>
                  <StatValue $valueColor={missedConnectionsValueColor}>
                    {formatPercentage(missedConnections)}
                  </StatValue>
                </StatTopSection>
                <StatTitle>{t('journeyDetails.transfer.missed')}</StatTitle>
              </StatBox>
            )}
          </StatsContainer>
          <InsightsButton />
        </StatContainer>
      )}
    </React.Fragment>  
  )
}

export default Transfer;
